// Walk Mode (FPV) state + constants. Pure data — reducer in
// state-machine.ts; pure math in movement.ts; orchestrator wiring
// (pointer-lock, key listeners, frame ticker) in orchestrator.ts.

export const WALK_SPEED = 3.0; // m/s
export const RUN_SPEED = 6.0; // m/s — Shift modifier deferred to 4.M
export const GRAVITY = 9.81; // m/s²
export const JUMP_VELOCITY = 4.0; // m/s
export const MOUSE_SENSITIVITY = 0.002; // rad/pixel

// MVP defaults: assume the splat dataset has Y=0 at floor level.
// Studio editor (post-MVP) will expose `metadata.floorHeight`. Auto-detect
// via 5° percentile of splat Y is deferred to 4.I2.
export const FLOOR_HEIGHT = 0;
export const EYE_HEIGHT = 1.65; // m — matches mockup "altezza 1.65m"
// Inc C — crouch: altezza occhi accovacciato + velocità di transizione (m/s).
export const CROUCH_EYE_HEIGHT = 0.9; // m
export const EYE_HEIGHT_TRANSITION_SPEED = 3.0; // m/s

// Clamp pitch to avoid gimbal flip at the poles.
export const PITCH_MAX = Math.PI / 2 - 0.01;

// Distance from floor below which we consider the player "on the floor"
// for jump eligibility (after gravity clamp).
export const ON_FLOOR_EPSILON = 1e-3;

export interface WalkState {
  active: boolean;
  eyeHeight: number;
  floorHeight: number;
  // WS5 PR3.1: indice ARRAY (NON numerico) della viewbox corrente nello
  // ViewboxState.available. null = walk in scena legacy senza viewbox
  // (orchestrator decide se permettere walk unbounded sul bbox scena o
  // disabilitare FP). Il clamp AABB viene applicato SOLO se questo e'
  // non-null (vedi orchestrator.ts walkTick PR3.2).
  currentViewboxArrayIdx: number | null;
}

export type WalkEvent =
  | { type: 'TOGGLE_ACTIVE' }
  | { type: 'EXIT' }
  // Inc C — crouch: aggiorna l'altezza occhi animata (eyeHeight) passando per
  // il funnel dispatchWalkEvent (così walkSnapshot/HUD restano sincronizzati).
  | { type: 'SET_EYE_HEIGHT'; eyeHeight: number }
  // WS5 PR3.1: viewbox sync. Emesso dall'orchestrator quando la viewbox
  // attiva cambia (load scena, SELECT, TELEPORT_START). Il walk reducer
  // aggiorna `currentViewboxArrayIdx` per il consumer walkTick.
  | { type: 'SET_VIEWBOX'; arrayIdx: number | null }
  // 2026-06-05: floor scene-specific. Pre-fix FLOOR_HEIGHT=0 hardcoded
  // assumeva splat normalizzato con pavimento a Y=0 — nessuna scena prod
  // ONIRI e' cosi'. Walk camera Y = floorHeight + eyeHeight + grav, quindi
  // senza questo override la camera spawnava sempre a Y=1.65 e finiva
  // clampata al top dell'headbox AABB (= cammino sul soffitto). Dispatched
  // dall'orchestrator quando viewbox SELECT/LOAD: floorHeight =
  // viewbox.default_camera.pos[1] - EYE_HEIGHT.
  | { type: 'SET_FLOOR_HEIGHT'; floorHeight: number };

export interface WalkSnapshot {
  active: boolean;
  eyeHeight: number;
  floorHeight: number;
  currentViewboxArrayIdx: number | null;
}

// WS5 PR4a — Mobile FP touch input slice.
// Emesso dal joystick virtuale (src/walk/joystick.ts) e consumato da
// computeVelocity (src/walk/movement.ts) in alternativa a walkKeysHeld.
// - forward/strafe sono componenti radiali normalizzate del joystick
//   nel range [-1, 1]; la lunghezza del vettore (forward, strafe)
//   coincide con magnitude ∈ [0, 1].
// - speed e' la velocita' lineare assoluta in m/s gia' selezionata dal
//   joystick in base a magnitude (soglia 0.7 → RUN). Il consumer NON
//   ri-applica selectWalkSpeed (non c'e' Shift mobile).
export interface WalkTouchInput {
  readonly forward: number;
  readonly strafe: number;
  readonly speed: number;
}

// WS5 PR4a — soglia magnitude joystick per il passaggio walk→run.
// Sotto 0.7 → WALK_SPEED, da 0.7 in poi → RUN_SPEED. Sotto JOYSTICK_DEADZONE
// → magnitude=0 (anti-jitter sul pollice).
export const JOYSTICK_RADIUS_PX = 80;
export const JOYSTICK_DEADZONE = 0.1;
export const JOYSTICK_RUN_THRESHOLD = 0.7;
